import { NavLink, useParams } from 'react-router-dom';
import { usePageMeta } from '@/lib/seo';
import { cn } from '@/lib/utils';
import {
  docSeoDescription,
  docSeoTitle,
  findDoc,
  PLAYBOOK_DOCS,
  TEMPLATE_DOCS,
  TROUBLESHOOTING,
  type DocKind,
} from '@/modules/docs/content';
import { DocView } from '@/modules/docs/DocView';
import { publicLink } from '@/modules/docs/links';
import { ReferenceIndex } from '@/modules/docs/ReferenceIndex';
import { MarketingPage } from '@/modules/marketing/MarketingPage';
import { SiteFooter } from '@/modules/marketing/SiteFooter';
import { SiteHeader } from '@/modules/marketing/SiteHeader';
import { NotFoundPage } from '@/modules/system/NotFoundPage';
import { PAGES } from '@/seo/pages';

/**
 * The public documentation reference: every playbook and template, and the troubleshooting guide.
 *
 * Public on purpose. Somebody deciding whether the platform can install PHP 8.4 on their Rocky Linux
 * server, or searching an error message it printed, should find the answer without an account; and
 * every page answered here is a support ticket that is never opened. The dashboard shows the same
 * pages under /help.
 */

export function PublicReferenceIndexPage({ kind }: { kind: 'playbook' | 'template' }) {
  const meta = kind === 'playbook' ? PAGES.docsPlaybooks : PAGES.docsTemplates;

  usePageMeta(meta);

  return (
    <MarketingPage eyebrow="Documentation" title={meta.title} standfirst={meta.description} wide>
      <div className="mt-14">
        <ReferenceIndex kind={kind} linkFor={publicLink} />
      </div>
    </MarketingPage>
  );
}

export function PublicDocPage({ kind }: { kind: DocKind }) {
  const { key } = useParams<{ key: string }>();
  const page = kind === 'troubleshooting' ? TROUBLESHOOTING : findDoc(kind, key ?? '');

  usePageMeta({
    title: page?.title ?? 'Page not found',
    seoTitle: page === undefined ? 'Page not found' : docSeoTitle(page),
    description: page === undefined ? 'This page could not be found.' : docSeoDescription(page),
    noindex: page === undefined,
  });

  if (page === undefined) {
    return <NotFoundPage />;
  }

  return (
    <div className="bg-background min-h-screen">
      <SiteHeader />

      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-10 lg:grid-cols-[248px_minmax(0,1fr)] lg:gap-12">
        <aside className="hidden lg:block" aria-label="Documentation">
          <div className="sticky top-24 max-h-[calc(100vh-7rem)] space-y-6 overflow-y-auto pr-2 text-sm">
            <SideSection title="Help">
              <SideLink to="/docs">Guides</SideLink>
              <SideLink to="/docs/troubleshooting">Troubleshooting</SideLink>
            </SideSection>
            <SideSection title="Playbooks">
              {PLAYBOOK_DOCS.map((doc) => (
                <SideLink key={doc.slug} to={`/docs/playbooks/${doc.slug}`}>
                  {doc.title}
                </SideLink>
              ))}
            </SideSection>
            <SideSection title="Templates">
              {TEMPLATE_DOCS.map((doc) => (
                <SideLink key={doc.slug} to={`/docs/templates/${doc.slug}`}>
                  {doc.title}
                </SideLink>
              ))}
            </SideSection>
          </div>
        </aside>

        <main className="min-w-0 max-w-4xl">
          <nav aria-label="Breadcrumb" className="text-muted-foreground mb-6 text-[13px]">
            <NavLink to="/docs" className="hover:text-foreground">
              Documentation
            </NavLink>
            {page.kind !== 'troubleshooting' && (
              <>
                {' / '}
                <NavLink to={page.kind === 'playbook' ? '/docs/playbooks' : '/docs/templates'} className="hover:text-foreground">
                  {page.kind === 'playbook' ? 'Playbooks' : 'Templates'}
                </NavLink>
              </>
            )}
          </nav>
          <DocView page={page} linkFor={publicLink} />
        </main>
      </div>

      <SiteFooter />
    </div>
  );
}

function SideSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-muted-foreground mb-2 text-xs font-semibold tracking-wider uppercase">{title}</p>
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

function SideLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <NavLink
        to={to}
        end
        className={({ isActive }) =>
          cn(
            'block rounded-md px-2 py-1.5 leading-snug transition-colors',
            isActive ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground',
          )
        }
      >
        {children}
      </NavLink>
    </li>
  );
}
